import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Link } from 'react-router'
import {
  CurrencyValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useSettlementMutations, useSettlements } from '../api'
import { LEAVING_REASON_LABELS, SETTLEMENT_STATUS_LABELS } from '../types'
import type { LeavingReason, SettlementStatus } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<SettlementStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  CALCULATED: 'warning',
  APPROVED: 'info',
  PAID: 'success',
  CANCELLED: 'neutral',
}

/** Everybody leaving, and what they are owed. */
export function SettlementsPage() {
  useBreadcrumb()
  const settlements = useSettlements()
  const { raise } = useSettlementMutations()

  const [draft, setDraft] = useState<{
    employeeId: string; leavingReason: LeavingReason; lastWorkingDate: string
    noticeServed: string; noticeStartDate: string; noticeEndDate: string; currency: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Final settlements"
        subtitle="End-of-service pay for people who are leaving."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                employeeId: '', leavingReason: 'RESIGNATION', lastWorkingDate: '',
                noticeServed: '', noticeStartDate: '', noticeEndDate: '', currency: 'AED',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New settlement
          </Button>
        }
      />

      <Card>
        {settlements.isPending && <Skeleton className="m-4 h-24" />}
        {settlements.isError && (
          <ErrorState error={settlements.error} onRetry={() => void settlements.refetch()} />
        )}
        {settlements.isSuccess && settlements.data.length === 0 && (
          <EmptyState
            title="Nobody is leaving"
            description="A settlement is raised, calculated from the entity's end-of-service terms, approved by somebody else, and then paid."
          />
        )}
        {settlements.isSuccess && settlements.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Person</th>
                  <th className="p-3 text-start font-medium">Reason</th>
                  <th className="p-3 text-start font-medium">Last day</th>
                  <th className="p-3 text-start font-medium">Service</th>
                  <th className="p-3 text-end font-medium">Net</th>
                  <th className="p-3 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {settlements.data.map((settlement) => (
                  <tr key={settlement.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <Link
                        className="font-mono underline underline-offset-2"
                        to={`/settlements/${settlement.id}`}
                      >
                        {settlement.reference}
                      </Link>
                    </td>
                    <td className="p-3">
                      {settlement.employeeName ?? settlement.employeeCode}
                    </td>
                    <td className="p-3 text-slate-600">
                      {LEAVING_REASON_LABELS[settlement.leavingReason as LeavingReason]
                        ?? settlement.leavingReason}
                    </td>
                    <td className="p-3 text-slate-600">{settlement.lastWorkingDate}</td>
                    <td className="p-3 text-slate-600">
                      {settlement.serviceYears}y {settlement.serviceMonths}m {settlement.serviceDays}d
                    </td>
                    <td className="p-3 text-end font-medium">
                      <CurrencyValue value={settlement.netAmount} currency={settlement.currency} />
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={settlement.status}
                        tone={STATUS_TONE[settlement.status as SettlementStatus] ?? 'neutral'}
                        label={SETTLEMENT_STATUS_LABELS[settlement.status as SettlementStatus]
                          ?? settlement.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="Raise a final settlement"
        description="Nothing is computed until you calculate it, against this entity's configured end-of-service terms."
        submitLabel="Raise it"
        loading={raise.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await raise.mutateAsync({
                employeeId: draft.employeeId,
                leavingReason: draft.leavingReason,
                lastWorkingDate: draft.lastWorkingDate,
                noticeServed: draft.noticeServed === '' ? null : draft.noticeServed === 'yes',
                noticeStartDate: draft.noticeStartDate || null,
                noticeEndDate: draft.noticeEndDate || null,
                currency: draft.currency,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That settlement could not be raised')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="settlement-employee">Person</Label>
              <EmployeePicker
                  id="settlement-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
            </div>
            <div>
              <Label htmlFor="settlement-reason">Reason for leaving</Label>
              <Select
                id="settlement-reason"
                className="w-full"
                value={draft.leavingReason}
                onChange={(event) =>
                  setDraft({ ...draft, leavingReason: event.target.value as LeavingReason })}
              >
                {Object.entries(LEAVING_REASON_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="settlement-last-day">Last working day</Label>
              <DatePicker
                id="settlement-last-day" value={draft.lastWorkingDate}
                onChange={(event) => setDraft({ ...draft, lastWorkingDate: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="settlement-notice">Notice served?</Label>
              <Select
                id="settlement-notice"
                className="w-full"
                value={draft.noticeServed}
                onChange={(event) => setDraft({ ...draft, noticeServed: event.target.value })}
              >
                <option value="">Not recorded</option>
                <option value="yes">Yes — worked through it</option>
                <option value="no">No — pay in lieu is due</option>
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Notice that was worked is paid through ordinary payroll, not here.
              </p>
            </div>
            {draft.noticeServed === 'no' && (
              <>
                <div>
                  <Label htmlFor="settlement-notice-from">Notice period from</Label>
                  <DatePicker
                    id="settlement-notice-from" value={draft.noticeStartDate}
                    onChange={(event) =>
                      setDraft({ ...draft, noticeStartDate: event.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="settlement-notice-to">Notice period to</Label>
                  <DatePicker
                    id="settlement-notice-to" value={draft.noticeEndDate}
                    onChange={(event) =>
                      setDraft({ ...draft, noticeEndDate: event.target.value })}
                  />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="settlement-currency">Currency</Label>
              <Input
                id="settlement-currency" maxLength={3} value={draft.currency}
                onChange={(event) =>
                  setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
